import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { MessageCircle, Heart, Bookmark, ArrowUpRight, ArrowLeft } from 'lucide-react';
import { getProject, getNextProject, FORCEP } from './projects';

/*
  SECTION 2 — PROJECT BENTO (1:1 with Illustrator master)
  Opens when a fan plate / project name is clicked.
  Every bento card carries mouse + entry parallax at a distinct depth.
*/

const useMouseParallax = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 18 });
  const sy = useSpring(my, { stiffness: 60, damping: 18 });
  const onMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  return { sx, sy, onMouseMove };
};

const depth = (sx, sy, f) => ({
  x: useTransform(sx, (v) => v * f),
  y: useTransform(sy, (v) => v * f),
});

const ProjectBento = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const project = getProject(slug);
  const next = getNextProject(slug);
  const [imgIndex, setImgIndex] = useState(0);
  const { sx, sy, onMouseMove } = useMouseParallax();

  // distinct parallax depths per card layer
  const heroLayer = depth(sx, sy, -22);
  const cardLayer = depth(sx, sy, -12);
  const flyerLayer = depth(sx, sy, -16);
  const chipLayer = depth(sx, sy, -30);

  useEffect(() => {
    setImgIndex(0);
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && navigate('/');
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  if (!project) {
    return (
      <div className="flex h-screen items-center justify-center bg-black text-white">
        <Link to="/" className="underline">PROJECT NOT FOUND — BACK TO FACTORY</Link>
      </div>
    );
  }

  const gallery = project.images;
  const mainImg = gallery[imgIndex % gallery.length];
  const sideImg = gallery[(imgIndex + 1) % gallery.length];
  const thumbImg = gallery[(imgIndex + 2) % gallery.length] || gallery[0];

  const cycle = () => setImgIndex((i) => (i + 1) % gallery.length);

  return (
    <div className="landing-font min-h-[100svh] bg-black px-3 py-3 text-white md:px-6 md:py-6" onMouseMove={onMouseMove}>
      <motion.div
        initial={{ opacity: 0, scale: 0.975, y: 24 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="mx-auto grid min-h-[calc(100svh-1.5rem)] max-w-[1500px] grid-cols-1 gap-3 overflow-hidden rounded-[2rem] bg-[#101010] p-3 md:min-h-[calc(100svh-3rem)] md:grid-cols-[42fr_58fr] md:gap-4 md:rounded-[2.6rem] md:p-4"
      >
        {/* ============ LEFT COLUMN ============ */}
        <div className="flex flex-col gap-3 md:gap-4">
          {/* Media card with project label + pills */}
          <motion.div
            style={cardLayer}
            className="relative h-[260px] overflow-hidden rounded-[1.4rem] bg-[#181818] md:h-[42%]"
          >
            <motion.img
              key={sideImg}
              src={sideImg}
              alt={project.name}
              initial={{ scale: 1.12, opacity: 0 }}
              animate={{ scale: 1.04, opacity: 1 }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
              className="h-full w-full object-cover"
            />
            {/* Project label — black pill, top/left as master */}
            <motion.span
              style={chipLayer}
              className="absolute left-5 top-1/3 bg-black px-4 py-2 text-[15px] font-bold tracking-[0.08em] text-white md:text-[17px]"
            >
              {project.name}
            </motion.span>
            {/* Right pills: ABRIL / BUILDING analog → year / status */}
            <motion.div style={chipLayer} className="absolute right-4 top-4 flex flex-col gap-2">
              <span className="rounded-2xl bg-[#e8e4dc] px-4 py-3 text-center text-[12px] font-bold tracking-wide text-black shadow-lg">
                {project.pillA}
              </span>
              <span className="rounded-2xl bg-[#8a8a86]/90 px-4 py-3 text-center text-[11px] font-bold tracking-wide text-white shadow-lg">
                {project.pillB}
              </span>
            </motion.div>
            <button
              type="button"
              onClick={cycle}
              aria-label="Next image"
              className="absolute bottom-4 right-4 flex h-10 w-10 items-center justify-center rounded-full border border-white/60 text-white transition-transform hover:scale-110"
            >
              <ArrowUpRight className="h-4 w-4" />
            </button>
          </motion.div>

          {/* Flyer / recommended card */}
          <motion.div
            style={flyerLayer}
            className="relative flex flex-1 flex-col overflow-hidden rounded-[1.4rem] p-6 md:p-8"
          >
            <div
              className="absolute inset-0"
              style={{ background: 'linear-gradient(160deg,#d24a28 0%,#c03d22 55%,#a93318 100%)' }}
              aria-hidden="true"
            />
            <div className="relative z-10 flex h-full flex-col">
              <div className="flex items-center justify-between text-[12px] tracking-wide text-white/80">
                <span>Recommended</span>
                <span className="tracking-[0.3em]">····</span>
              </div>

              {/* project "logo" lockup on the flyer */}
              <div className="mt-5 flex items-center gap-3">
                <img src={FORCEP.isoLight} alt="" className="h-7 w-auto opacity-90" />
                <span className="border border-white/40 px-2 py-1 text-[10px] font-bold tracking-[0.25em] text-white/90">
                  {project.category}
                </span>
              </div>

              <h1 className="mt-4 text-[26px] font-semibold leading-[1.08] tracking-tight md:text-[34px]">
                {project.taglineEs}
              </h1>
              <p className="mt-4 max-w-[46ch] text-[13px] leading-relaxed text-white/85 md:text-[14px]">
                {project.descEs}
              </p>

              <div className="mt-5 flex items-center gap-4">
                {project.link ? (
                  <a
                    href={project.link}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-full border border-white/70 px-5 py-2 text-[12px] font-semibold transition-colors hover:bg-white hover:text-[#c03d22]"
                  >
                    Read more
                  </a>
                ) : (
                  <button
                    type="button"
                    onClick={cycle}
                    className="rounded-full border border-white/70 px-5 py-2 text-[12px] font-semibold transition-colors hover:bg-white hover:text-[#c03d22]"
                  >
                    Read more
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => navigate(`/p/${next.slug}`)}
                  aria-label={`Next project: ${next.name}`}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-white/60 transition-transform hover:scale-110"
                >
                  <ArrowUpRight className="h-4 w-4" />
                </button>
              </div>

              <div className="mt-auto">
                <div className="flex items-center justify-between border-t border-white/25 pt-3 text-[12px] text-white/85">
                  <span className="max-w-[60%] truncate">{next.taglineEs}…</span>
                  <button type="button" onClick={() => navigate(`/p/${next.slug}`)} className="font-semibold hover:underline">
                    Next
                  </button>
                </div>
                <div className="mx-auto mt-3 h-[5px] w-24 overflow-hidden rounded-full bg-black/30">
                  <motion.span
                    className="block h-full rounded-full bg-black/80"
                    initial={{ width: 0 }}
                    animate={{ width: `${((imgIndex % gallery.length) + 1) * (100 / gallery.length)}%` }}
                    transition={{ duration: 0.4 }}
                  />
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* ============ RIGHT COLUMN — hero media ============ */}
        <motion.div style={heroLayer} className="relative h-[420px] overflow-hidden rounded-[1.4rem] bg-[#181818] md:h-auto">
          <motion.img
            key={mainImg}
            src={mainImg}
            alt={project.name}
            initial={{ scale: 1.15, opacity: 0 }}
            animate={{ scale: 1.05, opacity: 1 }}
            transition={{ duration: 0.9, ease: 'easeOut' }}
            className="h-full w-full object-cover"
          />

          {/* Forcep mark — top left of hero media */}
          <Link to="/" aria-label="Back to factory" className="absolute left-5 top-5">
            <img src={FORCEP.isoLight} alt="Forcep Fábrica" className="h-12 w-auto drop-shadow-lg md:h-16" />
          </Link>

          {/* Social rail — top right */}
          <motion.div style={chipLayer} className="absolute right-4 top-4 flex flex-col items-end gap-2">
            <span className="flex items-center gap-2 rounded-full bg-black/70 px-4 py-2 text-[12px] backdrop-blur">
              Comment <MessageCircle className="h-3.5 w-3.5" />
            </span>
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-black/70 backdrop-blur">
              <Heart className="h-4 w-4" />
            </span>
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-black/70 backdrop-blur">
              <Bookmark className="h-4 w-4" />
            </span>
          </motion.div>

          {/* Discover card — bottom right */}
          <motion.div
            style={flyerLayer}
            className="absolute bottom-4 right-4 w-[180px] rounded-[1.2rem] bg-[#0c0c0c]/92 p-4 backdrop-blur md:w-[200px]"
          >
            <button type="button" onClick={cycle} className="block w-full" aria-label="Next gallery image">
              <img src={thumbImg} alt="" className="mx-auto h-24 w-24 rounded-full object-cover md:h-28 md:w-28" />
            </button>
            <p className="mt-3 text-[13px] leading-snug text-white/90">
              Discover new<br />products
            </p>
            {project.link ? (
              <a
                href={project.link}
                target="_blank"
                rel="noreferrer"
                className="mt-3 block rounded-full bg-[#e8e4dc] px-4 py-2 text-center text-[12px] font-bold text-black transition-transform hover:scale-[1.03]"
              >
                Shop now
              </a>
            ) : (
              <button
                type="button"
                onClick={() => navigate(`/p/${next.slug}`)}
                className="mt-3 block w-full rounded-full bg-[#e8e4dc] px-4 py-2 text-center text-[12px] font-bold text-black transition-transform hover:scale-[1.03]"
              >
                Next project
              </button>
            )}
          </motion.div>

          {/* Back chip — bottom left */}
          <button
            type="button"
            onClick={() => navigate('/')}
            className="absolute bottom-4 left-4 flex items-center gap-2 rounded-full bg-black/70 px-4 py-2 text-[12px] backdrop-blur transition-colors hover:bg-white hover:text-black"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> FACTORY
          </button>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default ProjectBento;
